/**
 * Labels Section - Settings
 * Create, edit, and delete custom email labels
 */

import { useState, useEffect } from 'react';
import { IconPlus, IconPencil, IconTrash, IconLoader2, IconCheck, IconAlertCircle, IconCloud, IconMail, IconDownload } from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  fetchLabels,
  createLabel,
  updateLabel,
  deleteLabel,
  retryLabelSync,
  importGmailLabels,
  syncLabelEmails,
  type Label as LabelType,
  type SyncStatus,
} from '@/lib/cloudflare/labels-client';
import { invalidateLabelCache } from '@/components/label-editor';
import { getTextColorForBackground } from '@/lib/utils/label-colors';
import { Tooltip } from '@/components/ui/tooltip';
import { useCFAuth } from '@/lib/auth/cf-auth-context';

// Gmail's exact supported label colors
const COLORS = [
  { name: 'Berry', value: '#b65775' },
  { name: 'Red', value: '#cc3a21' },
  { name: 'Orange', value: '#ffad47' },
  { name: 'Yellow', value: '#fbe983' },
  { name: 'Green', value: '#16a766' },
  { name: 'Teal', value: '#44b984' },
  { name: 'Blue', value: '#4a86e8' },
  { name: 'Purple', value: '#a479e2' },
  { name: 'Pink', value: '#e07798' },
  { name: 'Gray', value: '#e7e7e7' },
];

// Sync status indicator - clickable to sync with Gmail
function SyncStatusIcon({
  status,
  error,
  onClick,
  isLoading
}: {
  status: SyncStatus;
  error: string | null;
  onClick?: () => void;
  isLoading?: boolean;
}) {
  if (isLoading) {
    return <IconLoader2 className="h-3 w-3 animate-spin text-muted-foreground" />;
  }

  const isClickable = onClick && (status === 'local_only' || status === 'failed');
  const baseClass = isClickable ? 'cursor-pointer hover:opacity-80' : '';

  switch (status) {
    case 'synced':
      return (
        <Tooltip content="Synced with Gmail">
          <span><IconCheck className="h-3 w-3 text-green-500" /></span>
        </Tooltip>
      );
    case 'pending':
      return (
        <Tooltip content="Syncing...">
          <span><IconLoader2 className="h-3 w-3 text-yellow-500 animate-spin" /></span>
        </Tooltip>
      );
    case 'failed':
      return (
        <Tooltip content={`${error || 'Sync failed'} - Click to retry`}>
          <button onClick={onClick} className={baseClass}>
            <IconAlertCircle className="h-3 w-3 text-red-500" />
          </button>
        </Tooltip>
      );
    case 'local_only':
      return (
        <Tooltip content="Click to sync with Gmail">
          <button onClick={onClick} className={baseClass}>
            <IconCloud className="h-3 w-3 text-muted-foreground/50" />
          </button>
        </Tooltip>
      );
    default:
      return null;
  }
}

export function LabelsSection() {
  const { accessToken } = useCFAuth();
  const [labels, setLabels] = useState<LabelType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLabel, setEditingLabel] = useState<LabelType | null>(null);
  const [deleteConfirmLabel, setDeleteConfirmLabel] = useState<LabelType | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [syncingLabelId, setSyncingLabelId] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [syncingEmailsLabelId, setSyncingEmailsLabelId] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [color, setColor] = useState(COLORS[6].value);
  const [description, setDescription] = useState('');

  const loadLabels = async () => {
    try {
      const labelsData = await fetchLabels();
      setLabels(labelsData.labels);
    } catch (error) {
      console.error('Failed to load labels:', error);
      toast.error('Failed to load labels');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadLabels();
  }, []);

  const openCreateDialog = () => {
    setEditingLabel(null);
    setName('');
    setColor(COLORS[6].value);
    setDescription('');
    setIsDialogOpen(true);
  };

  const openEditDialog = (label: LabelType) => {
    setEditingLabel(label);
    setName(label.name);
    setColor(label.color);
    setDescription(label.description || '');
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('Label name is required');
      return;
    }

    setIsSaving(true);
    try {
      if (editingLabel) {
        const updated = await updateLabel(editingLabel.id, {
          name: name.trim(),
          color,
          description: description.trim() || undefined,
          accessToken: accessToken || undefined,
        });
        setLabels(labels.map(l => l.id === updated.id ? { ...l, ...updated } : l));
        toast.success('Label updated');
      } else {
        const created = await createLabel({
          name: name.trim(),
          color,
          description: description.trim() || undefined,
          accessToken: accessToken || undefined,
        });
        setLabels([...labels, created]);
        toast.success('Label created');
      }
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Failed to save label:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save label');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirmLabel) return;
    try {
      await deleteLabel(deleteConfirmLabel.id, accessToken || undefined);
      setLabels(labels.filter(l => l.id !== deleteConfirmLabel.id));
      toast.success('Label deleted');
    } catch (error) {
      console.error('Failed to delete label:', error);
      toast.error('Failed to delete label');
    } finally {
      setDeleteConfirmLabel(null);
    }
  };

  const handleRetrySync = async (label: LabelType) => {
    if (!accessToken) {
      toast.error('Sign in to sync labels');
      return;
    }
    setSyncingLabelId(label.id);
    try {
      const updated = await retryLabelSync(label.id, accessToken);
      setLabels(labels.map(l => l.id === updated.id ? { ...l, ...updated } : l));
      toast.success('Label synced');
    } catch (error) {
      console.error('Failed to sync label:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to sync');
    } finally {
      setSyncingLabelId(null);
    }
  };

  const handleImportGmailLabels = async () => {
    if (!accessToken) {
      toast.error('Sign in to import Gmail labels');
      return;
    }
    setIsImporting(true);
    try {
      const result = await importGmailLabels(accessToken);
      invalidateLabelCache();
      await loadLabels();
      toast.success(`Imported ${result.imported}, updated ${result.updated}`);
    } catch (error) {
      console.error('Failed to import Gmail labels:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to import');
    } finally {
      setIsImporting(false);
    }
  };

  const handleSyncLabelEmails = async (label: LabelType) => {
    if (!accessToken) {
      toast.error('Sign in to sync emails');
      return;
    }
    setSyncingEmailsLabelId(label.id);
    try {
      const result = await syncLabelEmails(label.id, accessToken);
      invalidateLabelCache();
      await loadLabels();
      toast.success(`Linked ${result.synced} of ${result.inDatabase} emails to "${label.name}" (${result.total} in Gmail)`);
    } catch (error) {
      console.error('Failed to sync label emails:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to sync emails');
    } finally {
      setSyncingEmailsLabelId(null);
    }
  };

  return (
    <div>
      {/* Header with actions */}
      <div className="flex items-center justify-between border-b border-border/30 mb-3">
        {/* Title */}
        <div className="flex items-center gap-4">
          <span className="text-xs pb-2 border-b-2 border-foreground text-foreground font-medium -mb-px">
            Your Labels ({labels.length})
          </span>
        </div>
        {/* Actions */}
        <div className="flex items-center gap-3">
          {accessToken && (
            <button
              onClick={handleImportGmailLabels}
              disabled={isImporting}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
            >
              {isImporting ? <IconLoader2 className="h-3 w-3 animate-spin" /> : <IconDownload className="h-3 w-3" />}
              Import from Gmail
            </button>
          )}
          <button
            onClick={openCreateDialog}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
          >
            <IconPlus className="h-3 w-3" />
            Create Label
          </button>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <IconLoader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : labels.length === 0 ? (
        <div className="text-center py-8 text-xs text-muted-foreground">
          No labels yet. Create one to get started.
        </div>
      ) : (
        <div className="divide-y divide-border/20">
          {labels.map((label) => {
            const isAiLabel = label.source === 'ai' || label.id?.startsWith('ai_');
            const isGmailLabel = label.source === 'gmail' || !!label.gmail_label_id;

            return (
              <div key={label.id} className="py-2 flex items-center gap-3 hover:bg-black/[0.02] dark:hover:bg-white/[0.02]">
                <span
                  className="font-mono text-[10px] px-1.5 py-0.5 rounded shrink-0"
                  style={{
                    backgroundColor: label.color,
                    color: getTextColorForBackground(label.color)
                  }}
                >
                  {label.name}
                </span>
                {/* Source indicator */}
                <span className={`text-[9px] px-1.5 py-0.5 rounded shrink-0 ${
                  isAiLabel
                    ? 'bg-purple-500/10 text-purple-600'
                    : isGmailLabel
                    ? 'bg-red-500/10 text-red-600'
                    : 'bg-blue-500/10 text-blue-600'
                }`}>
                  {isAiLabel ? 'AI' : isGmailLabel ? 'Gmail' : 'Custom'}
                </span>
                {!isAiLabel && (
                  <SyncStatusIcon
                    status={label.sync_status || 'local_only'}
                    error={label.sync_error ?? null}
                    onClick={accessToken ? () => handleRetrySync(label) : undefined}
                    isLoading={syncingLabelId === label.id}
                  />
                )}
                <span className="flex-1" />
                <span className="text-xs text-muted-foreground font-mono" title="Emails with this label">{label.email_count || 0}</span>
                {label.gmail_label_id && accessToken && (
                  <button
                    onClick={() => handleSyncLabelEmails(label)}
                    disabled={syncingEmailsLabelId === label.id}
                    className="text-muted-foreground hover:text-foreground p-1"
                    title="Sync emails with this label from Gmail"
                  >
                    {syncingEmailsLabelId === label.id ? (
                      <IconLoader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <IconMail className="h-3 w-3" />
                    )}
                  </button>
                )}
                {!isAiLabel && (
                  <>
                    <button onClick={() => openEditDialog(label)} className="text-muted-foreground hover:text-foreground p-1">
                      <IconPencil className="h-3 w-3" />
                    </button>
                    <button onClick={() => setDeleteConfirmLabel(label)} className="text-muted-foreground hover:text-destructive p-1">
                      <IconTrash className="h-3 w-3" />
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Label Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingLabel ? 'Edit Label' : 'Create Label'}</DialogTitle>
            <DialogDescription>
              {editingLabel ? 'Update your label details' : 'Create a new label for organizing emails'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Travel, Receipts, Work"
              />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex gap-1.5 flex-wrap">
                {COLORS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    className={`w-6 h-6 rounded transition-opacity ${
                      color === c.value ? 'ring-1 ring-offset-1 ring-foreground/50' : 'opacity-60 hover:opacity-100'
                    }`}
                    style={{ backgroundColor: c.value }}
                    onClick={() => setColor(c.value)}
                    title={c.name}
                  />
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Input
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={isSaving}>
              {isSaving && <IconLoader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingLabel ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Label Confirmation */}
      <AlertDialog open={!!deleteConfirmLabel} onOpenChange={() => setDeleteConfirmLabel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Label</AlertDialogTitle>
            <AlertDialogDescription>
              Delete "{deleteConfirmLabel?.name}"? This removes it from all emails.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
